import { AlertTriangle, Info, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../cx.js';

/**
 * Something the person has to read before carrying on.
 *
 * The title is required, and it is required to be a *sentence about this
 * product* — "Impossible de consulter votre agenda", not "Erreur". §46: an
 * error in a product that answers a salon's telephone has to say what stopped
 * working and what will happen because of it, or the owner's only option is to
 * ring us.
 *
 * `role="alert"` only for failures. Announcing an informational panel
 * interrupts whatever a screen-reader user was reading, which is rude for
 * something they would have reached anyway.
 */

export type AlertKind = 'info' | 'warning' | 'error';

const STYLES: Record<AlertKind, string> = {
  info: 'border-line-strong bg-surface-muted text-ink',
  warning: 'border-warning bg-warning-wash text-ink',
  error: 'border-danger bg-danger-wash text-ink',
};

const ICONS: Record<AlertKind, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
};

const ICON_TONE: Record<AlertKind, string> = {
  info: 'text-ink-muted',
  warning: 'text-warning',
  error: 'text-danger',
};

export function Alert({
  kind = 'info',
  title,
  children,
  action,
}: {
  readonly kind?: AlertKind;
  readonly title: string;
  readonly children?: ReactNode;
  /** What to do about it. An error with no way forward is a dead end. */
  readonly action?: ReactNode;
}): React.JSX.Element {
  const Icon = ICONS[kind];
  return (
    <div
      className={cx('rounded-card border p-4 sm:p-5', STYLES[kind])}
      {...(kind === 'error' ? { role: 'alert' } : {})}
    >
      <div className="flex gap-3">
        <Icon
          size={20}
          strokeWidth={1.75}
          aria-hidden="true"
          className={cx('mt-0.5 shrink-0', ICON_TONE[kind])}
        />
        <div className="min-w-0 flex-1">
          <p className="text-ui font-medium">{title}</p>
          {children !== undefined && (
            <div className="mt-1 text-ui-sm text-ink-muted">{children}</div>
          )}
          {action !== undefined && <div className="mt-4">{action}</div>}
        </div>
      </div>
    </div>
  );
}
